/**
 * Chargement du pack d'assets (un GLB, une texture atlas KTX2, meshopt) et
 * matériaux partagés. Tous les objets du décor utilisent le même matériau :
 * l'atlas évite les changements de texture entre draw calls.
 *
 * Classes de matériau encodées dans l'alpha de l'atlas (voir ASSET_SPEC.md) :
 *   255 = couleur normale, 128 = émissif la nuit (vitres, lanternes),
 *   0 = teinte par instance (veste des skieurs, sièges, panneaux).
 */
import * as THREE from 'three/webgpu';
import { abs, attribute, float, mix, smoothstep, texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

export interface PackLod {
  node: string;
  triangles: number;
  vertices: number;
}
export interface PackAsset {
  category: string;
  lods: PackLod[];
  bounds: { min: [number, number, number]; max: [number, number, number]; radius: number };
  budget: number[];
}
export interface PackManifest {
  version: number;
  file: string;
  assets: Record<string, PackAsset>;
}

export interface AssetEntry {
  name: string;
  category: string;
  geometries: THREE.BufferGeometry[];
  bounds: PackAsset['bounds'];
  triangles: number[];
}

/** Intensité de l'éclairage nocturne des vitres (0 le jour, 1 la nuit). */
export const nightUniform = uniform(0);

export class AssetLibrary {
  readonly entries = new Map<string, AssetEntry>();
  atlas: THREE.Texture | null = null;
  fallback = false;
  /** Matériau des objets instanciés (teinte par instance). */
  instanced!: THREE.MeshStandardNodeMaterial;
  /** Matériau des objets non instanciés (fond de montagne, fantôme). */
  plain!: THREE.MeshStandardNodeMaterial;

  get(name: string): AssetEntry {
    const e = this.entries.get(name);
    if (!e) throw new Error(`Asset absent du pack : ${name}`);
    return e;
  }

  has(name: string): boolean {
    return this.entries.has(name);
  }
}

function atlasMaterial(atlas: THREE.Texture | null, tinted: boolean): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial();
  m.roughness = 0.82;
  m.metalness = 0;
  if (atlas) {
    const tex = texture(atlas, uv());
    const a = tex.a;
    const tintMask = float(1).sub(smoothstep(0.05, 0.3, a));
    const emissiveMask = float(1).sub(smoothstep(0.12, 0.3, abs(a.sub(0.5))));
    const tint = tinted ? attribute('tint', 'vec3') : vec3(1, 1, 1);
    let base = mix(tex.rgb, tex.rgb.mul(tint), tintMask);
    // Vitres sombres le jour, chaudes la nuit.
    base = mix(base, base.mul(0.35), emissiveMask.mul(float(1).sub(nightUniform)));
    m.colorNode = base;
    m.emissiveNode = tex.rgb.mul(emissiveMask).mul(nightUniform).mul(1.8);
  } else {
    m.vertexColors = true;
    if (tinted) m.colorNode = attribute('color', 'vec3').mul(attribute('tint', 'vec3'));
  }
  return m;
}

/**
 * Worker de transcodage KTX2 servi comme fichier (`public/basis/ktx2-worker.js`, généré par
 * `tools/assets/ktx2-worker.mjs`) au lieu de l'URL blob: que KTX2Loader fabrique : une URL blob:
 * hérite de la CSP de la page, qui devrait alors autoriser eval. Ici seul le worker l'autorise.
 * Fixer `transcoderPending` avant le premier chargement court-circuite `init()`.
 */
function useStaticKtx2Worker(loader: KTX2Loader): void {
  loader.transcoderPending = fetch('/basis/basis_transcoder.wasm')
    .then((r) => {
      if (!r.ok) throw new Error(`transcodeur KTX2 : HTTP ${r.status}`);
      return r.arrayBuffer();
    })
    .then((bin) => {
      loader.transcoderBinary = bin;
      loader.workerPool.setWorkerCreator(() => {
        const worker = new Worker('/basis/ktx2-worker.js');
        const copy = bin.slice(0);
        worker.postMessage({ type: 'init', config: loader.workerConfig, transcoderBinary: copy }, [copy]);
        return worker;
      });
    });
}

export async function loadAssets(renderer: THREE.WebGPURenderer, onProgress: (p: number) => void): Promise<AssetLibrary> {
  const lib = new AssetLibrary();
  try {
    const manifest = (await (await fetch('/assets/pack.json')).json()) as PackManifest;
    const ktx2 = new KTX2Loader().setTranscoderPath('/basis/');
    ktx2.detectSupport(renderer);
    useStaticKtx2Worker(ktx2);
    const loader = new GLTFLoader().setKTX2Loader(ktx2).setMeshoptDecoder(MeshoptDecoder);
    const gltf = await loader.loadAsync(`/assets/${manifest.file}`, (ev) => {
      if (ev.total) onProgress(ev.loaded / ev.total);
    });
    const byName = new Map<string, THREE.Mesh>();
    gltf.scene.updateMatrixWorld(true);
    gltf.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      // Le nom du nœud glTF est porté par le mesh ou par son parent.
      const name = /_LOD\d$/.test(mesh.name) ? mesh.name : mesh.parent && /_LOD\d$/.test(mesh.parent.name) ? mesh.parent.name : mesh.name;
      byName.set(name, mesh);
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!lib.atlas && mat.map) lib.atlas = mat.map;
    });
    for (const [name, asset] of Object.entries(manifest.assets)) {
      const geometries: THREE.BufferGeometry[] = [];
      for (const lod of asset.lods) {
        const mesh = byName.get(lod.node);
        if (!mesh) continue;
        const g = mesh.geometry as THREE.BufferGeometry;
        if (!mesh.matrixWorld.equals(new THREE.Matrix4())) g.applyMatrix4(mesh.matrixWorld);
        g.computeBoundingSphere();
        geometries.push(g);
      }
      if (geometries.length) {
        lib.entries.set(name, { name, category: asset.category, geometries, bounds: asset.bounds, triangles: asset.lods.map((l) => l.triangles) });
      }
    }
    if (lib.atlas) {
      lib.atlas.colorSpace = THREE.SRGBColorSpace;
      lib.atlas.anisotropy = 4;
    } else {
      // Texture illisible (CSP, transcodeur absent) : couleur neutre plutôt que des modèles noirs.
      console.warn('[assets] atlas KTX2 non chargé, couleurs de secours utilisées');
      for (const e of lib.entries.values()) for (const g of e.geometries) if (!g.hasAttribute('color')) colored(g, 0xdfe6ea);
    }
  } catch (e) {
    console.warn('[assets] pack introuvable ou illisible, géométrie de secours utilisée', e);
    lib.fallback = true;
  }
  addFallbacks(lib);
  lib.instanced = atlasMaterial(lib.fallback ? null : lib.atlas, true);
  lib.plain = atlasMaterial(lib.fallback ? null : lib.atlas, false);
  onProgress(1);
  return lib;
}

/**
 * Géométrie de secours (formes simples) pour chaque asset manquant : le jeu
 * reste jouable même si le pipeline d'assets n'a pas encore tourné.
 */
function addFallbacks(lib: AssetLibrary): void {
  const box = (w: number, h: number, d: number, color: number, y = h / 2) => colored(new THREE.BoxGeometry(w, h, d).translate(0, y, 0), color);
  const cone = (r: number, h: number, color: number) => colored(new THREE.ConeGeometry(r, h, 7).translate(0, h / 2 + 1, 0), color);
  const specs: Record<string, [string, () => THREE.BufferGeometry, number]> = {
    skier_ski: ['skier', () => box(0.5, 1.6, 0.4, 0xffffff), 1.2],
    skier_sit: ['skier', () => box(0.5, 1.0, 0.8, 0xffffff, 0.2), 1.1],
    skier_walk: ['skier', () => box(0.5, 1.75, 0.4, 0xffffff), 1.2],
    tree_fir_a: ['tree', () => cone(2.2, 9, 0x33553e), 5],
    tree_fir_b: ['tree', () => cone(2.8, 7, 0x4e7a5a), 4.5],
    rock_a: ['rock', () => box(1.4, 0.9, 1.2, 0x7c8b95), 1],
    rock_b: ['rock', () => box(2.8, 1.1, 2.2, 0x7c8b95), 1.8],
    rock_c: ['rock', () => box(3.5, 3.5, 2.8, 0x465864), 2.5],
    lamp_post: ['prop', () => box(0.25, 4.5, 0.25, 0x22363f), 2.3],
    sign_piste: ['prop', () => box(0.7, 2.2, 0.1, 0xffffff), 1.1],
    snow_cannon: ['prop', () => box(1.6, 3.4, 1.6, 0xaab6bd), 2.1],
    chair_4: ['carrier', () => box(2.6, 0.5, 0.8, 0xffffff, -2.6), 2.2],
    gondola_cabin: ['carrier', () => box(1.9, 2.2, 1.8, 0xffffff, -2.1), 1.9],
    drag_hanger: ['carrier', () => box(0.15, 2.4, 0.15, 0x465864, -1.2), 1.3],
    pylon: ['pylon', () => box(0.8, 11.5, 0.8, 0xaab6bd), 6.4],
    drag_pylon: ['pylon', () => box(0.4, 7, 0.4, 0xaab6bd), 3.8],
    lift_station: ['station', () => box(9, 7, 14, 0xa97e4f), 9.3],
    drag_station: ['station', () => box(6, 4, 4.5, 0xa97e4f), 4.1],
    chalet_accueil: ['building', () => box(26, 10, 14, 0xcda97c), 15.4],
    restaurant: ['building', () => box(18, 8, 12, 0xcda97c), 11.7],
    shop_rental: ['building', () => box(10, 6, 8, 0xcda97c), 6.6],
    ticket_office: ['building', () => box(6, 4, 4, 0xcda97c), 4.6],
    welcome_area: ['building', () => box(20, 3, 12, 0xebdcc5), 12.3],
    hotel: ['building', () => box(30, 14, 16, 0xcda97c), 17.9],
    residence: ['building', () => box(34, 12, 14, 0xd8c3a2), 18.4],
    ski_school: ['building', () => box(24, 7, 14, 0xcda97c), 13.9],
    first_aid: ['building', () => box(10, 5, 8, 0xd8dde0), 6.4],
    snack_bar: ['building', () => box(12, 5, 10, 0xcda97c), 7.8],
    groomer_garage: ['building', () => box(24, 8, 16, 0xaab6bd), 14.4],
    // Dalles basses : enrobé gris pour le parking, eau bleutée pour la retenue.
    parking: ['building', () => box(48, 0.4, 30, 0x5d6870), 28.3],
    reservoir: ['building', () => box(64, 0.6, 44, 0x6f9bb3), 38.9],
    chalet_small_a: ['house', () => box(9, 7, 8, 0xb88a5a), 6.1],
    chalet_small_b: ['house', () => box(11, 8, 9, 0xc49a6c), 7.2],
    piste_pole: ['marker', () => box(0.12, 1.8, 0.12, 0xffffff), 0.9],
    groomer: ['vehicle', () => box(5.2, 3.2, 8.7, 0xb8432f), 4.6],
    backdrop_mountains: ['backdrop', () => colored(new THREE.CylinderGeometry(6000, 7000, 900, 48, 1, true).translate(0, 450, 0), 0x7fa3b5), 7000],
  };
  for (const [name, [category, make, radius]] of Object.entries(specs)) {
    if (lib.entries.has(name)) continue;
    const g = make();
    lib.entries.set(name, {
      name,
      category,
      geometries: [g, g],
      bounds: { min: [-radius, 0, -radius], max: [radius, radius * 2, radius], radius },
      triangles: [g.index ? g.index.count / 3 : 0, g.index ? g.index.count / 3 : 0],
    });
  }
}

function colored(g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const c = new THREE.Color(hex);
  const n = g.attributes.position!.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return g;
}
